import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ZodError, z } from "zod";
import { useAuth } from "../context/auth";
import { apiUrls } from "../helpers/apiUrls";
import { FORM_ERR } from "../helpers/constants";
import { useAxiosPrivate } from "../hooks/useAxiosPrivate";
import { statusApiSchema } from "../schemas/status";
import { conditionalObject, paginatedResponse } from "../utils";

export const LeadFormSchema = z.object({
    personName: z.string({ message: FORM_ERR.person_name_required }).nonempty({
        message: FORM_ERR.person_name_required,
    }),
    personEmail: z
        .string()
        .email("add_lead_form_wrong_email_info")
        .or(z.string().optional().nullable()),
    personSurname: z.string({ message: FORM_ERR.surname_required }).nonempty({
        message: FORM_ERR.person_name_required,
    }),
    personPhone: z.string().optional().nullable(),
    sellerComment: z.string().optional(),
    leadSource: z
        .string()
        .min(1, {
            message: "add_lead_form_contact_source_required_error",
        })
        .default(""),
    leadCaretakerId: z.string().optional(),
    leadStatus: z.string().optional().nullable(),
});

export function useLeadsStatuses(options = {}) {
    const axiosPriv = useAxiosPrivate();

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["leadsStatuses"],
        queryFn: () => {
            return leadsApi.getStatuses(axiosPriv);
        },
        throwOnError: (error) => error instanceof ZodError,
    });

    return useQuery({
        ...queryOpts,
    });
}

export function useLeadFailureReasons(options = {}) {
    const axiosPriv = useAxiosPrivate();

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["failureReasons"],
        queryFn: () => {
            return leadsApi.getLeadFailureReasons(axiosPriv);
        },
        throwOnError: (error) => error instanceof ZodError,
    });

    return useQuery({
        ...queryOpts,
    });
}

export function useLeadsSourceChannels(options = {}) {
    const axiosPriv = useAxiosPrivate();

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["leadsSourceChannels"],
        queryFn: () => {
            return leadsApi.getSourceChannels(axiosPriv);
        },
        throwOnError: (error) => error instanceof ZodError,
    });

    return useQuery({
        ...queryOpts,
    });
}
export function useLeads(params = new URLSearchParams(), options = {}) {
    const axiosPriv = useAxiosPrivate();

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["leads", Object.fromEntries(params.entries())],
        queryFn: () => {
            return leadsApi.getLeads(axiosPriv, params);
        },
        throwOnError: (error) => error instanceof ZodError,
    });

    return useQuery({
        ...queryOpts,
    });
}

export function useLeadChartDistribution(params = new URLSearchParams(), options = {}) {
    const axiosPriv = useAxiosPrivate();

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["leads-chart", Object.fromEntries(params.entries())],
        queryFn: () => {
            return leadsApi.getLeadsChartDistribution(axiosPriv, params);
        },
        throwOnError: (error) => error instanceof ZodError,
    });

    return useQuery({
        ...queryOpts,
    });
}

export function useLeadAggregation(params = new URLSearchParams(), options = {}) {
    const axiosPriv = useAxiosPrivate();

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["leads-aggregation", Object.fromEntries(params.entries())],
        queryFn: () => {
            return leadsApi.getLeadsAggregation(axiosPriv, params);
        },
        throwOnError: (error) => error instanceof ZodError,
    });

    return useQuery({
        ...queryOpts,
    });
}

export function useAddLeadMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();

    return useMutation({
        ...options,
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: ["leads"] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: (lead) => {
            return leadsApi.addLead(axiosPriv, lead);
        },
    });
}

export function useRemoveLeadMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();

    return useMutation({
        ...options,
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: ["leads"] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: (leadId) => {
            return leadsApi.deleteLead(axiosPriv, leadId);
        },
    });
}

export function useEditLeadMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();

    return useMutation({
        ...options,
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: ["leads"] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ({ leadId, lead }) => {
            return leadsApi.editLeadLead(axiosPriv, leadId, lead);
        },
    });
}

export function useAddSellerToLeadMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    // @ts-ignore
    const { user } = useAuth();

    return useMutation({
        ...options,
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: ["leads"] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ({ leadId, sellerId }) => {
            if (leadId && sellerId) {
                return leadsApi.addSellerToLead(axiosPriv, {
                    leadId,
                    sellerId,
                    creatorId: user.user_id,
                });
            }
            return Promise.reject(new Error("leadId and sellerId must not be none"));
        },
    });
}

export function useEditLeadPersonMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();

    return useMutation({
        ...options,
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: ["leads"] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ({ personId, leadPerson }) => {
            return leadsApi.editLeadPerson(axiosPriv, personId, leadPerson);
        },
    });
}

export function useImportFileMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();

    return useMutation({
        ...options,
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: ["leads"] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ({ file, fileNote }) => {
            return leadsApi.importFile(axiosPriv, { file, fileNote });
        },
    });
}

export function useCorrectLeadPersonMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();

    return useMutation({
        ...options,
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: ["leads"] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: (payload) => {
            return leadsApi.correctLeadPerson(axiosPriv, payload);
        },
    });
}

export function usePersonLeads(personId, options = {}) {
    const axiosPriv = useAxiosPrivate();

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["personLeads", personId],
        queryFn: () => {
            return leadsApi.getPersonLeads(axiosPriv, personId);
        },
    });

    return useQuery({
        ...queryOpts,
    });
}

export function useLeadsChartFilteringData(options = {}) {
    const axiosPriv = useAxiosPrivate();

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["leads_chart_filtering_data"],
        queryFn: () => {
            return leadsApi.getChartFiltering(axiosPriv);
        },
        throwOnError: (error) => error instanceof ZodError,
    });

    return useQuery({
        ...queryOpts,
    });
}

export function useEditReasonMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();

    return useMutation({
        ...options,
        onSuccess: (data, variables, context) => {
            if (variables.agreement_id) {
                queryClient.invalidateQueries({ queryKey: ["agreements"] });
            }
            if (variables.booking_id) {
                queryClient.invalidateQueries({ queryKey: ["customerBookingsDetails"] });
            }
            if (variables.offer_id) {
                queryClient.invalidateQueries({ queryKey: ["customerOffers"] });
            }
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: (data) => {
            return leadsApi.editReason(axiosPriv, data);
        },
    });
}

const LeadFailureReasonSchema = z.object({
    id: z.string(),
    reason_name: z.string(),
    reason_name_eng: z.string(),
});

const SourceChannelResponseSchema = z.object({
    channel_name: z.string(),
    channel_name_eng: z.string(),
    channel_note: z.string(),
});

export const leadsApiSchema = {
    getLeadFailureReasonsResponse: paginatedResponse(LeadFailureReasonSchema),
    getSourceChannelsResponse: paginatedResponse(SourceChannelResponseSchema),
};

export const leadsApi = {
    getLeads: (axios, params) => {
        return axios
            .get(apiUrls.sales.leads.url, {
                params,
            })
            .then((response) => response.data);
    },

    getLeadsChartDistribution: (axios, params) => {
        return axios
            .get(apiUrls.sales.leads.chartDistribution, {
                params,
            })
            .then((response) => response.data);
    },

    getLeadsAggregation: (axios, params) => {
        return axios
            .get(apiUrls.sales.leads.leadAggregation, {
                params,
            })
            .then((response) => response.data);
    },

    async getStatuses(axios) {
        const response = await axios.get(apiUrls.sales.leads.getStatusesUrl);
        return statusApiSchema.getStatusesResponse.parse(response.data);
    },

    async getLeadFailureReasons(axios) {
        const response = await axios.get(apiUrls.sales.leads.getLeadFailureReasonsUrl);
        return leadsApiSchema.getLeadFailureReasonsResponse.parse(response.data);
    },

    async getSourceChannels(axios) {
        const response = await axios.get(apiUrls.customers.channels.all);
        return leadsApiSchema.getSourceChannelsResponse.parse(response.data);
    },

    addLead(axios, lead) {
        return axios
            .post(apiUrls.sales.leads.url, {
                person_id: lead.personId,
                ...(lead.personId
                    ? {}
                    : {
                          person: {
                              person_name: lead.personName,
                              person_surname: lead.personSurname,
                              phone: lead.personPhone ? { phone_number: lead.personPhone } : null,
                              email: lead.personEmail ? { email_address: lead.personEmail } : null,
                          },
                      }),
                lead: {
                    lead_status: lead.leadStatus,
                    lead_source: lead.leadSource,
                    lead_invest: lead.leadInvestment,
                    lead_status_reason: lead.leadStatusReason,
                    lead_status_note: lead.statusNote,
                    lead_note_from_sales: lead.sellerComment,
                    lead_note_from_source: lead.sourceComment,
                    seller_id: lead.leadCaretakerId,
                    product_ids: lead.productIds,
                },
                send_email: lead.send_email
            })
            .then((response) => response.data);
    },

    deleteLead(axios, leadId) {
        return axios
            .delete(apiUrls.sales.leads.deleteLeadUrl(leadId))
            .then((response) => response.data);
    },

    editLeadLead(axios, leadId, lead) {
        return axios
            .put(apiUrls.sales.leads.lead(leadId), {
                lead_status: lead.leadStatus,
                lead_status_note: lead.statusNote,
                lead_source: lead.leadSource,
                lead_invest: lead.leadInvestment,
                lead_status_reason: lead.leadStatusReason,
                lead_note_from_sales: lead.sellerComment,
                product_ids: lead.productIds,
                ...conditionalObject({ person_id: lead.person_id }, () => !!lead.person_id), // TODO rem this
                ...conditionalObject({ seller_id: lead.seller_id }, () => !!lead.seller_id), // TODO rem this
            })
            .then((response) => response.data);
    },

    editLeadPerson(axios, personId, lead) {
        return axios.put(apiUrls.sales.persons.person(personId), {
            email: lead.personEmail ? { email_address: lead.personEmail } : null,
            phone: lead.personPhone ? { phone_number: lead.personPhone } : null,
            person_name: lead.personName,
            person_surname: lead.personSurname,
        });
    },

    addSellerToLead(axios, { leadId, sellerId, creatorId }) {
        return axios.post(apiUrls.sales.leads.addSellerToLeadUrl, {
            leadsell_lead: leadId,
            leadsell_seller: sellerId,
            creator: creatorId,
            leadsell_active: true,
        });
    },

    importFile(axios, { file, fileNote }) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("note", fileNote);
        return axios
            .post(apiUrls.sales.leads.importFile, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            })
            .then((response) => response.data);
    },

    correctLeadPerson(axios, payload) {
        return axios
            .post(apiUrls.sales.persons.correctLeadPerson, payload)
            .then((response) => response.data);
    },

    getPersonLeads(axios, personId) {
        const params = new URLSearchParams({ personId });
        return axios.get(apiUrls.sales.leads.url, { params }).then((response) => response.data);
    },

    async getChartFiltering(axios) {
        const response = await axios.get(apiUrls.sales.leads.chartfilteringData);
        return response.data
    },

    editReason(axios, data) {
        return axios
            .post(apiUrls.sales.editReason, data)
            .then((response) => response.data);
    },
};
